import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { chatAPI } from 'services/api';
import { RootState } from 'store/rootReducer';

import { CanalInterface, TipoCanalEnum } from './canais';
import { ContatoInterface } from './contatos';
import { EquipeInterface } from './equipes';

export type AgendamentoStatusTypes =
  | 'AGENDADO'
  | 'CANCELADO'
  | 'CONCLUIDO'
  | 'NAO_CONCLUIDO';

export enum AgendamentoStatusEnum {
  AGENDADO = 'AGENDADO',
  CANCELADO = 'CANCELADO',
  CONCLUIDO = 'CONCLUIDO',
  NAO_CONCLUIDO = 'NAO_CONCLUIDO',
}

export const AgendamentoStatusValues: Record<AgendamentoStatusTypes, string> = {
  [AgendamentoStatusEnum.AGENDADO]: 'Agendados',
  [AgendamentoStatusEnum.CANCELADO]: 'Cancelados',
  [AgendamentoStatusEnum.CONCLUIDO]: 'Concluidos',
  [AgendamentoStatusEnum.NAO_CONCLUIDO]: 'Não concluidos',
};

export declare interface AgendamentoInterface {
  id: string;
  agendadoPara: string;
  agendadoParaDate: string;
  agendadoParaTime: string;
  canal: CanalInterface;
  comentario: string | null;
  contato: ContatoInterface;
  equipe: EquipeInterface;
  mensagem: string;
  status: AgendamentoStatusTypes;
  observacao: string | null;
}

export declare interface AgendamentoInterfaceV2 {
  id: string;
  message: string;
  observation: string | null;
  comment: string | null;
  status: AgendamentoStatusTypes;
  scheduledAt: string;
  team: {
    id: string;
    name: string;
  };
  contact: {
    id: string;
    name: string;
    number: string;
    photoUrl: string | null;
    blockedAt?: string | null;
  };
  channel: {
    id: string;
    name: string;
    number: string;
    type: TipoCanalEnum;
    isHybridMode: boolean;
  };
  attendant: {
    id: string;
    name: string;
  };
}

type AgendamentosState = {
  agendamentos: AgendamentoInterface[];
  agendamentosCursor: CursorInterface;
  loadingAgendamentos: boolean;

  agendamentosV2: AgendamentoInterfaceV2[];
  agendamentosV2InfoPage: { page: number; total: number };
  loadingAgendamentosV2: boolean;

  idsDeAgendamentosSelecionados: string[];
};

// async thunks

export const fetchAgendamentos = createAsyncThunk(
  'agendamentos/fetchAgendamentos',
  async (params?: {
    filter?: string;
    filters?: string;
    cursor?: CursorInterface;
  }) => {
    const response = await chatAPI.get('atendimentos-agendados', {
      params: {
        filter: params?.filter,
        filters: params?.filters,
        ...params?.cursor,
      },
    });
    return {
      data: response.data.data as AgendamentoInterface[],
      cursor: response.data.cursor as CursorInterface,
    };
  },
);

export const fetchAgendamentosV2 = createAsyncThunk(
  'v2/agendamentos/fetchAgendamentosV2',
  async (params: {
    page?: number;
    limit?: number;
    status?: AgendamentoStatusTypes;
    attendantId: string;
    date?: string;
    teamId?: string;
    channelId?: string;
    contact?: string;
  }) => {
    const response = await chatAPI.get('v2/scheduled-atendimentos', {
      params: {
        page: params?.page ?? 1,
        limit: params?.limit ?? 25,
        ...params,
      },
    });

    return {
      data: response.data.data as AgendamentoInterfaceV2[],
      total: response.data.total as number,
      page: params?.page ?? 1,
    };
  },
);

export const criarAgendamento = createAsyncThunk(
  'agendamentos/criarAgendamento',
  async (
    values: {
      contatoId: string;
      canalId: string;
      equipeId: string;
      agendarParaDate: string | Date | null;
      agendarParaTime: string | Date | null;
      mensagem: string;
      comentario: string | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await chatAPI.post('atendimentos-agendados', values);
      return response.data as AgendamentoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const editarAgendamento = createAsyncThunk(
  'agendamentos/editarAgendamento',
  async (
    values: {
      contatoId: string;
      canalId: string;
      equipeId: string;
      agendarParaDate: string | Date | null;
      agendarParaTime: string | Date | null;
      mensagem: string;
      comentario: string | null;
      agendamentoId: string;
    },
    { rejectWithValue },
  ) => {
    const { agendamentoId } = values;
    try {
      const response = await chatAPI.put(
        `atendimentos-agendados/${agendamentoId}`,
        values,
      );
      return response.data as AgendamentoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const alterarDataAgendamento = createAsyncThunk(
  'agendamentos/alterarDataAgendamento',
  async (
    values: {
      agendamentoId: string;
      agendarParaDate: string | Date | null;
      agendarParaTime: string | Date | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const { agendamentoId, agendarParaDate, agendarParaTime } = values;
      const response = await chatAPI.put(
        `atendimentos-agendados/${agendamentoId}`,
        { agendarParaDate, agendarParaTime },
      );
      return response.data.data as AgendamentoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const cancelarAgendamento = createAsyncThunk(
  'agendamentos/cancelarAgendamento',
  async (agendamentoId: string, { rejectWithValue }) => {
    try {
      const response = await chatAPI.put(
        `atendimentos-agendados/${agendamentoId}/cancelar`,
      );
      return response.data as AgendamentoInterface;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const cancelarAgendamentosEmLote = createAsyncThunk(
  'v2/agendamentos/cancelarAgendamentosEmLote',
  async (ids: string[], { rejectWithValue }) => {
    try {
      const response = await chatAPI.put('v2/scheduled-atendimentos/cancel', {
        scheduledAtendimentoIds: ids,
      });
      return response.data as AgendamentoInterface[];
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// reducer

const initialState = {
  agendamentos: [],
  agendamentosCursor: { afterCursor: '', beforeCursor: '' },
  loadingAgendamentos: false,

  agendamentosV2: [],
  agendamentosV2InfoPage: { page: 1, total: 0 },
  loadingAgendamentosV2: false,

  idsDeAgendamentosSelecionados: [],
  selecaoDeAgendamentosHabilitada: false,
} as AgendamentosState;

export const agendamentosSlice = createSlice({
  name: 'agendamentos',
  initialState,
  reducers: {
    setIdsAgendamentosSelecionados(state, action) {
      state.idsDeAgendamentosSelecionados = action.payload;
    },
    resetIdsAgendamentosSelecionados(state) {
      state.idsDeAgendamentosSelecionados =
        initialState.idsDeAgendamentosSelecionados;
    },
    resetAgendamentos(state) {
      state.agendamentos = initialState.agendamentos;
      state.agendamentosCursor = initialState.agendamentosCursor;
    },
    resetAgendamentosV2(state) {
      state.agendamentosV2 = initialState.agendamentosV2;
      state.agendamentosV2InfoPage = initialState.agendamentosV2InfoPage;
    },
  },
  extraReducers(builder) {
    // Fetch agendamentos utilizados na pagina de agendamentos
    builder.addCase(fetchAgendamentos.pending, state => {
      state.loadingAgendamentos = true;
    });
    builder.addCase(fetchAgendamentos.fulfilled, (state, action) => {
      state.agendamentos = state.agendamentos.concat(action.payload.data);
      state.agendamentosCursor = action.payload.cursor;
      state.loadingAgendamentos = false;
    });

    // Fetch agendamentos v2 utilizados na pagina de agendamentos
    builder.addCase(fetchAgendamentosV2.pending, state => {
      state.loadingAgendamentosV2 = true;
    });
    builder.addCase(fetchAgendamentosV2.fulfilled, (state, action) => {
      if (action.payload.page > 1) {
        state.agendamentosV2 = [
          ...state.agendamentosV2,
          ...action.payload.data,
        ];
      } else {
        state.agendamentosV2 = action.payload.data;
      }

      state.agendamentosV2InfoPage.page = action.payload.page;
      state.agendamentosV2InfoPage.total = action.payload.total;
      state.loadingAgendamentosV2 = false;
    });
    builder.addCase(fetchAgendamentosV2.rejected, state => {
      state.loadingAgendamentosV2 = false;
    });

    // Alterar a data do agendamento
    builder.addCase(alterarDataAgendamento.pending, state => {
      state.loadingAgendamentos = true;
    });
    builder.addCase(alterarDataAgendamento.fulfilled, (state, action) => {
      const agendamentoId = action.payload.id;
      const index = state.agendamentos.findIndex(
        ({ id }) => id === agendamentoId,
      );
      if (index > -1) {
        state.agendamentos[index].agendadoPara = action.payload.agendadoPara;
      }
    });
    builder.addCase(alterarDataAgendamento.rejected, state => {
      state.loadingAgendamentos = false;
    });
  },
});

// actions

export const {
  setIdsAgendamentosSelecionados,
  resetIdsAgendamentosSelecionados,
  resetAgendamentos,
  resetAgendamentosV2,
} = agendamentosSlice.actions;

// selectors

export const agendamentosSelector = (state: RootState) => state.agendamentos;

export default agendamentosSlice.reducer;
